import { useEffect } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  LayoutDashboard, Users, CreditCard, FileText, LogOut, Cpu,
} from 'lucide-react'
import { useAppStore } from '../../store/useAppStore'
import { adminApi } from './adminApi'

interface AdminMe {
  id: string
  line_uid: string
  display_name: string | null
  role: 'admin' | 'super_admin'
}

export default function AdminLayout() {
  const navigate = useNavigate()
  const { lineUid, profile } = useAppStore()

  const { data: me, isLoading, error } = useQuery({
    queryKey: ['admin', 'me', lineUid],
    queryFn: () => adminApi.me(lineUid!) as Promise<AdminMe>,
    enabled: !!lineUid,
    retry: false,
  })

  useEffect(() => {
    if (!isLoading && (error || !me)) {
      const t = setTimeout(() => navigate('/', { replace: true }), 2000)
      return () => clearTimeout(t)
    }
  }, [isLoading, error, me, navigate])

  if (!lineUid || isLoading) {
    return (
      <div className="min-h-dvh flex items-center justify-center"
           style={{ background: '#f5f1e8' }}>
        <div className="text-center">
          <div className="text-5xl mb-3 animate-pulse">🔐</div>
          <p style={{ color: '#6b6354' }}>กำลังตรวจสอบสิทธิ์...</p>
        </div>
      </div>
    )
  }

  if (error || !me) {
    return (
      <div className="min-h-dvh flex items-center justify-center p-6"
           style={{ background: '#f5f1e8' }}>
        <div className="text-center max-w-sm">
          <div className="text-6xl mb-4">🚫</div>
          <h1 className="text-xl font-bold mb-2" style={{ color: '#2d2820' }}>
            ไม่มีสิทธิ์เข้าถึง
          </h1>
          <p className="text-sm mb-6" style={{ color: '#6b6354' }}>
            หน้านี้สำหรับผู้ดูแลระบบเท่านั้น<br/>
            กำลังพากลับหน้าหลัก...
          </p>
        </div>
      </div>
    )
  }

  const navItems = [
    { to: '/admin/dashboard',    icon: LayoutDashboard, label: 'ภาพรวม' },
    { to: '/admin/users',        icon: Users,           label: 'ผู้ใช้งาน' },
    { to: '/admin/transactions', icon: CreditCard,      label: 'ธุรกรรม' },
    { to: '/admin/logs',         icon: FileText,        label: 'บันทึก' },
    { to: '/admin/api-costs',   icon: Cpu,             label: 'API Costs' },
  ]

  return (
    <div className="min-h-dvh flex" style={{ background: '#f5f1e8' }}>

      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-60 flex-col fixed top-0 left-0 h-dvh"
             style={{ background: '#1a1d18', color: '#f0ead8' }}>
        <div className="p-5 flex items-center gap-2.5 border-b"
             style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
          <img src="/logo.png" alt=""
               className="w-9 h-9 rounded-full object-cover"
               style={{ border: '1.5px solid #b8945a' }} />
          <div>
            <div className="font-bold text-base" style={{ color: '#d4b884' }}>Admin Panel</div>
            <div className="text-[10px] opacity-70">Amulet Digit</div>
          </div>
        </div>

        <nav className="flex-1 p-3 flex flex-col gap-1">
          {navItems.map(item => (
            <NavLink key={item.to} to={item.to}
                     className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors"
                     style={({ isActive }) => ({
                       background: isActive ? 'rgba(184, 148, 90, 0.18)' : 'transparent',
                       color: isActive ? '#d4b884' : '#b8b09a',
                       fontWeight: isActive ? 600 : 400,
                     })}>
              <item.icon size={18} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
          <div className="flex items-center gap-3 p-2.5 rounded-xl"
               style={{ background: 'rgba(255,255,255,0.04)' }}>
            <div className="w-9 h-9 rounded-full overflow-hidden flex items-center justify-center text-base font-bold"
                 style={{ background: 'linear-gradient(135deg, #d4b884 0%, #b8945a 100%)' }}>
              {profile?.avatar_url ? (
                <img src={profile.avatar_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <span>👑</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold truncate">{me.display_name ?? 'Admin'}</div>
              <div className="text-[10px] opacity-70 truncate">
                {me.role === 'super_admin' ? '⭐ Super Admin' : 'Admin'}
              </div>
            </div>
          </div>
          <button onClick={() => navigate('/')}
                  className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-xs"
                  style={{ background: 'rgba(255,255,255,0.04)', color: '#b8b09a' }}>
            <LogOut size={14} /> ออกจาก Admin
          </button>
        </div>
      </aside>

      {/* Mobile Top Bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 px-4 py-3 flex items-center justify-between"
           style={{ background: '#1a1d18', color: '#f0ead8' }}>
        <div className="flex items-center gap-2">
          <img src="/logo.png" alt="" className="w-8 h-8 rounded-full" />
          <div className="font-bold text-sm" style={{ color: '#d4b884' }}>Admin</div>
        </div>
        <button onClick={() => navigate('/')}
                className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-lg"
                style={{ background: 'rgba(255,255,255,0.08)' }}>
          <LogOut size={12} /> ออก
        </button>
      </div>

      {/* Mobile Bottom Nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 flex"
           style={{ background: '#1a1d18', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        {navItems.map(item => (
          <NavLink key={item.to} to={item.to}
                   className="flex-1 flex flex-col items-center gap-0.5 py-2.5"
                   style={({ isActive }) => ({
                     color: isActive ? '#d4b884' : '#8a8270',
                   })}>
            <item.icon size={18} />
            <span className="text-[9px]">{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <main className="flex-1 md:ml-60 pt-14 md:pt-0 pb-16 md:pb-0">
        <Outlet />
      </main>
    </div>
  )
}
